import React, { useState } from 'react';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { translate } from '../../../services/translate';
import FilterInput from '../../../components/Filter/components/FilterInput';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { DispatchParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { dispatchService } from '../../../services/central-api/dispatch';
import { useAuth } from '../../../context/Auth';
import { Pagination } from '../../../components/Pagination';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton from '../../../components/DropdownButton';
import CustomButton from '../../../components/CustomButton';
import PatrolIndicatorModal from './PatrolIndicatorModal';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { RootNavigation } from '../../../Routes';

export default function PatrolIndicators({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<DispatchParamList, 'PatrolIndicators'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['PatrolIndicators']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedIndicator, setSelectedIndicator] = useState<PatrolIndicator>();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);


    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getReportTags', filters],
        queryFn: () => dispatchService.getPatrolIndicators(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    async function deleteIndicator(indicator: PatrolIndicator) {
        try {
            await dispatchService.deletePatrolIndicator(indicator.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setIsDeleteModalVisible(false);
            refetch();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('patrolIndicators')}
                actions={
                    <CustomButton
                        text={translate('register')}
                        onPress={() => {
                            setSelectedIndicator(undefined);
                            setModalVisible(true);
                        }} />
                }
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredPatrolIndicators')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: 'ID',
                                selector: row => row.id,
                                grow: 0
                            },
                            {
                                name: translate('code'),
                                selector: row => row.code,
                                wrap: true,
                                grow: 4
                            },
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true,
                                grow: 4
                            },
                            {
                                name: translate('actingBody'),
                                selector: row => row.actingBody.name,
                                wrap: true,
                            },
                            {
                                cell: row => (
                                    <DropdownButton
                                        actions={[
                                            {
                                                content: translate('editInformation'),
                                                onPress: () => {
                                                    setSelectedIndicator(row);
                                                    setModalVisible(true);
                                                }
                                            },
                                            {
                                                content: translate('delete'),
                                                variant: 'danger',
                                                onPress: () => {
                                                    setSelectedIndicator(row);
                                                    setIsDeleteModalVisible(true);
                                                },
                                            }
                                        ]}
                                    />
                                ),
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={data?.count ?? 0}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                    />}
                />
            </Card>
            <PatrolIndicatorModal
                isModalVisible={isModalVisible}
                onClose={() => {
                    setModalVisible(false);
                    refetch();
                }}
                patrolIndicatorId={selectedIndicator?.id}
            />

            {selectedIndicator &&
                <DeleteModal
                    isModalVisible={isDeleteModalVisible}
                    itemName={`${selectedIndicator.code} - ${selectedIndicator.name}`}
                    setModalVisible={() => setIsDeleteModalVisible(false)}
                    onSubmit={async () => {
                        try {
                            if (selectedIndicator) {
                                await deleteIndicator(selectedIndicator);
                            }
                        } catch (err) {
                            console.error(err);
                        }
                    }}
                    onCancel={() => {
                        setSelectedIndicator(undefined);
                    }} />
            }
        </Container>
    );
}
